import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Eye, LifeBuoy, MessageSquareWarning } from "lucide-react";
import api, { PREVIEW_REFUSED_EVENT } from "../../lib/api";
import { TONE_CLASSES } from "../../lib/portalKinds";
import { reportHref } from "../../lib/portalTickets";
import { bannerLine } from "../../lib/portalPreview";

/** What every portal page shares: the ground, the width, the card, the button. */

export const LOGO_COLOR = "/assets/logo-fped-color.svg";

export const primaryBtn =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] " +
  "text-white font-semibold px-5 py-2.5 transition-colors disabled:opacity-60 focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-[#965EA7] focus-visible:ring-offset-2";

export const secondaryBtn =
  "inline-flex items-center justify-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 " +
  "text-sm font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] disabled:opacity-60";

export const fieldCls =
  "w-full rounded-xl border border-[#E5E7EB] px-3.5 py-2.5 text-[15px] text-[#373A45] " +
  "focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20";

/**
 * The portal's pages once somebody is in are the wide ones: they end with
 * "Raportează o problemă" (14.10.2026), unless the page says `report={false}`
 * — the reports' own pages, and the home page, which has it in its footer.
 * `widest` is the guide's (19.10.2026): its list and the article side by side.
 */
export function Shell({ children, wide = false, report = true, widest = false }) {
  return (
    <div className="min-h-screen bg-[#F5F6F6] px-4 sm:px-6 py-10" data-testid="portal-page">
      <PreviewBanner />
      <div className={`mx-auto w-full ${widest ? "max-w-5xl" : wide ? "max-w-3xl" : "max-w-md"}`}>
        {children}
        {wide && report && (
          <footer className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[#E5E7EB] pt-5">
            <GuideLink />
            <ReportLink />
          </footer>
        )}
      </div>
    </div>
  );
}

// One question per page load: is this a team preview? (22.10.2026)
let previewAsked = null;

/**
 * The band at the top during a team preview: whose portal this is, that
 * nothing can be changed, until when, and the way out. Absent otherwise.
 */
function PreviewBanner() {
  const [pv, setPv] = useState(null);
  const [refused, setRefused] = useState("");
  useEffect(() => {
    previewAsked = previewAsked || api.get("/portal/preview").then((r) => r.data.preview).catch(() => null);
    let alive = true;
    previewAsked.then((x) => { if (alive) setPv(x); });
    let timer;
    const onRefused = (e) => {
      setRefused(e.detail);
      clearTimeout(timer);
      timer = setTimeout(() => setRefused(""), 5000);
    };
    window.addEventListener(PREVIEW_REFUSED_EVENT, onRefused);
    return () => { alive = false; clearTimeout(timer); window.removeEventListener(PREVIEW_REFUSED_EVENT, onRefused); };
  }, []);
  if (!pv) return null;
  const leave = async () => {
    try { await api.post("/portal/preview/exit"); } catch { /* the cookie goes anyway on expiry */ }
    previewAsked = null;
    // A tab the admin opened closes and leaves the admin where it was; one the
    // preview took over goes back to the admin.
    if (window.opener) window.close();
    else window.location.href = "/admin/portal";
  };
  return (
    <div className="sticky top-0 z-50 -mx-4 -mt-10 mb-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-[#373A45] px-4 py-2.5 text-sm text-white sm:-mx-6"
      role="status" data-testid="portal-preview-banner">
      <Eye size={15} aria-hidden="true" />
      <span className="font-semibold">{bannerLine(pv)}</span>
      <button type="button" onClick={leave}
        className="rounded-full border border-white/40 px-3 py-0.5 text-xs font-semibold hover:bg-white hover:text-[#373A45]"
        data-testid="portal-preview-exit">
        Ieși
      </button>
      {refused && (
        <span className="basis-full text-center text-xs font-semibold text-[#FDE68A]" role="alert"
          data-testid="portal-preview-refused">
          {refused}
        </span>
      )}
    </div>
  );
}

/** "Ghidul portalului" (19.10.2026): how the portal works, for this person's roles. */
export function GuideLink({ query = "", children = "Ghidul portalului" }) {
  return (
    <Link to={query ? `/portal/ghid?q=${encodeURIComponent(query)}` : "/portal/ghid"}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#965EA7] hover:underline"
      data-testid="portal-guide-link">
      <LifeBuoy size={14} aria-hidden="true" /> {children}
    </Link>
  );
}

/** "Raportează o problemă": to the form, knowing the page it was pressed on. */
export function ReportLink() {
  const { pathname } = useLocation();
  return (
    <Link to={reportHref(pathname)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#965EA7]"
      data-testid="portal-report-link">
      <MessageSquareWarning size={14} /> Raportează o problemă
    </Link>
  );
}

export function Card({ children }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E5E7EB] px-7 sm:px-10 py-10 shadow-sm">
      {children}
    </div>
  );
}

export function Chip({ tone = "neutral", children }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONE_CLASSES[tone]}`}>
      {children}
    </span>
  );
}
